import { useEffect, useRef, type ReactNode } from "react";

import { Icon } from "@/components/icons";
/** Centered modal ("semi pop-up") — header / scrolling body / footer as
 *  three separate flex rows (a sticky footer inside the scroll area was
 *  measured overlapping the last form row — never again). Modals STACK:
 *  a modal opened from inside a modal sits above it with a lighter dim, and
 *  Esc / overlay-click / ✕ close ONLY the topmost one (LIFO).
 *
 *  Creation flows return their record to the opener via `onCreated`-style
 *  callbacks in the caller; this component owns only the surface + stack. */

// module-level LIFO stack of close handlers — Esc always hits the top one
const stack: Array<() => void> = [];
let listening = false;
function ensureListener() {
  if (listening) return;
  listening = true;
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && stack.length) stack[stack.length - 1]();
  });
}

/** How much room the dialog takes. Three steps, not a free-form width, so the
 *  console keeps a small set of dialog sizes instead of one per call site.
 *
 *  `xwide` exists for a TABLE in a dialog. The reach matrix (D-126) is a grid
 *  of a company column, up to seven bench columns and the air column; at
 *  `wide` (940px) it fitted only by horizontal scrolling, which hides the
 *  columns on the right -- in a control whose whole value is seeing every tick
 *  at once. */
export type ModalSize = "default" | "wide" | "xwide";

const MODAL_WIDTH: Record<ModalSize, string> = {
  default: "min(600px,94vw)",
  wide: "min(940px,96vw)",
  xwide: "min(1400px,98vw)",
};

export function Modal({ onClose, title, crumb, footer, wide = false, size, children }: {
  onClose: () => void;
  title: ReactNode;
  /** breadcrumb shown beside the title: where the result will land */
  crumb?: ReactNode;
  footer?: ReactNode;
  /** Kept for the call sites that already pass it; `size="wide"` is the same
   *  thing. `size` wins when both are given. */
  wide?: boolean;
  size?: ModalSize;
  children: ReactNode;
}) {
  const depth = useRef(0);
  useEffect(() => {
    ensureListener();
    stack.push(onClose);
    depth.current = stack.length;
    return () => { const i = stack.lastIndexOf(onClose); if (i >= 0) stack.splice(i, 1); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const stacked = depth.current > 1;

  return (
    <div onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{ position: "fixed", inset: 0, zIndex: 80 + depth.current, display: "flex",
        alignItems: "center", justifyContent: "center",
        background: stacked ? "rgba(15,23,32,.25)" : "rgba(15,23,32,.4)" }}>
      <div role="dialog" aria-modal="true" style={{
        background: "var(--surface)", border: "1px solid var(--line2)", borderRadius: 14,
        boxShadow: stacked ? "0 20px 60px rgba(0,0,0,.35)" : "var(--shadow)",
        width: MODAL_WIDTH[size ?? (wide ? "wide" : "default")], maxHeight: "92vh",
        display: "flex", flexDirection: "column", overflow: "hidden" }}>
        <div style={{ padding: "16px 22px", borderBottom: "1px solid var(--line)", display: "flex",
          alignItems: "center", gap: 12, flexWrap: "wrap", flex: "0 0 auto" }}>
          <span style={{ fontWeight: 700 }}>{title}</span>
          {crumb && <span style={{ fontSize: 11.5, color: "var(--t-mute)" }}>{crumb}</span>}
          <button className="btn ghost" style={{ marginLeft: "auto" }} onClick={onClose} aria-label="Close"><Icon name="close" /></button>
        </div>
        <div style={{ padding: "18px 22px", overflowY: "auto", flex: "1 1 auto", minHeight: 0 }}>
          {children}
        </div>
        {footer && (
          <div style={{ flex: "0 0 auto", borderTop: "1px solid var(--line2)", padding: "12px 22px",
            display: "flex", gap: 10, alignItems: "center", justifyContent: "flex-end" }}>
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}
